import { useParams, Link, useNavigate } from "react-router-dom";
import { 
  Calendar, 
  Clock, 
  ArrowLeft, 
  ArrowRight,
  BookOpen,
  Share2,
  Phone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { blogPosts } from "./Blog";
import useSEO from "@/hooks/useSEO";
import { sanitizeHTML } from "@/utils/sanitize";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const post = blogPosts.find(p => p.slug === slug);
  const currentIndex = blogPosts.findIndex(p => p.slug === slug);
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;
  
  // Related posts (same category, excluding current)
  const relatedPosts = blogPosts
    .filter(p => p.category === post?.category && p.slug !== slug)
    .slice(0, 3);

  // SEO with blog data
  useSEO(null, post);

  if (!post) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-semibold text-navy mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-6">The article you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button className="bg-orange hover:bg-orange-hover text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        // Share was cancelled by user - this is expected behavior
        if (err.name !== 'AbortError' && process.env.NODE_ENV === 'development') {
        if (process.env.NODE_ENV === 'development') {
          console.error('Share failed:', err);
        }
        }
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div data-testid="blog-post-page" className="">
      {/* Hero */}
      <section className="bg-navy py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
          
          <div className="flex items-center gap-3 text-orange text-sm font-medium mb-4">
            <BookOpen className="w-4 h-4" />
            {post.category}
          </div>
          
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight mb-6">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-gray-400">
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(post.date).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {post.readTime}
            </span>
            <button 
              onClick={handleShare}
              className="flex items-center gap-2 hover:text-orange transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {post.image && (
        <div className="max-w-4xl mx-auto px-6 lg:px-8 -mt-6 relative z-10">
          <div className="aspect-video rounded-xl overflow-hidden shadow-lg">
            <img 
              src={post.image} 
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-12">
            {/* Main Content */}
            <article className="lg:col-span-3 prose prose-lg max-w-none">
              <div 
                className="blog-content"
                dangerouslySetInnerHTML={{ 
                  __html: sanitizeHTML(post.content
                    .replace(/^## (.*$)/gim, '<h2 class="font-heading text-2xl font-semibold text-navy mt-8 mb-4">$1</h2>')
                    .replace(/^### (.*$)/gim, '<h3 class="font-heading text-xl font-semibold text-navy mt-6 mb-3">$1</h3>')
                    .replace(/^\*\*(.*?)\*\*/gim, '<strong class="font-semibold text-navy">$1</strong>')
                    .replace(/^\* (.*$)/gim, '<li class="text-gray-700 ml-4">$1</li>')
                    .replace(/^- (.*$)/gim, '<li class="text-gray-700 ml-4">$1</li>')
                    .replace(/^(\d+)\. (.*$)/gim, '<li class="text-gray-700 ml-4"><span class="font-semibold text-orange">$1.</span> $2</li>')
                    .split('\n\n')
                    .map(p => {
                      if (p.startsWith('<h') || p.startsWith('<li') || p.startsWith('<strong')) return p;
                      if (p.trim() === '') return '';
                      return `<p class="text-gray-700 leading-relaxed mb-4">${p}</p>`;
                    })
                    .join(''))
                }}
              />
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* CTA Box */}
                <div className="bg-navy rounded-xl p-6 text-center">
                  <h3 className="font-heading text-lg font-semibold text-white mb-3">
                    Need Help?
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Connect with a trusted Minnesota roofing professional.
                  </p>
                  <Link to="/#get-started">
                    <Button className="w-full bg-orange hover:bg-orange-hover text-white">
                      <Phone className="w-4 h-4 mr-2" />
                      Get Connected
                    </Button>
                  </Link>
                </div>

                {/* Categories */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-heading font-semibold text-navy mb-4">Categories</h3>
                  <div className="space-y-2">
                    {[...new Set(blogPosts.map(p => p.category))].map(cat => (
                      <Link
                        key={cat}
                        to={`/blog?category=${encodeURIComponent(cat)}`}
                        className="block text-gray-600 hover:text-orange transition-colors text-sm"
                      >
                        {cat}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-8 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {prevPost ? (
              <Link 
                to={`/blog/${prevPost.slug}`}
                className="flex items-center gap-3 text-gray-600 hover:text-orange transition-colors group"
              >
                <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                <div className="text-left">
                  <span className="text-sm text-gray-400 block">Previous</span>
                  <span className="font-medium line-clamp-1 max-w-[200px]">{prevPost.title}</span>
                </div>
              </Link>
            ) : <div />}
            
            {nextPost ? (
              <Link 
                to={`/blog/${nextPost.slug}`}
                className="flex items-center gap-3 text-gray-600 hover:text-orange transition-colors group text-right"
              >
                <div>
                  <span className="text-sm text-gray-400 block">Next</span>
                  <span className="font-medium line-clamp-1 max-w-[200px]">{nextPost.title}</span>
                </div>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="font-heading text-2xl font-semibold text-navy mb-6">
              Related Articles
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map(relatedPost => (
                <Link
                  key={relatedPost.slug}
                  to={`/blog/${relatedPost.slug}`}
                  className="group bg-gray-50 rounded-xl border border-gray-200 p-6 hover:bg-white hover:shadow-lg hover:border-orange/30 transition-all"
                >
                  <div className="text-orange text-sm font-medium mb-2">
                    {relatedPost.category}
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-navy mb-2 group-hover:text-orange transition-colors line-clamp-2">
                    {relatedPost.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2">
                    {relatedPost.excerpt}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
